/* Phase 6A-4 — F1. driver.js wrapper + multi-page tour engine.
 *
 * Each tour step looks like:
 *   { id, route?, selector, titleKey, bodyKey, side?, align?, optional? }
 *
 * The engine:
 *  1. Reads the script for the active role
 *  2. For each step:
 *      - if `route` is set and current location differs → navigate via setHash or
 *        actually navigate (we accept a `navigate` fn at start-time)
 *      - waitForElement(selector, 3000) → if not found AND optional → skip silently
 *  3. Calls driver.js per-step so we can route between steps
 *  4. On completion → POST /api/me/tour/complete; on skip → POST /api/me/tour/skip
 *
 * driver.js v1 API surface:
 *   import { driver } from 'driver.js';
 *   const d = driver({ ...options });
 *   d.drive() / d.moveNext() / d.movePrevious() / d.destroy()
 */
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "@/styles/tour.css";
import api from "@/lib/api";
import i18n from "@/i18n";
import { scripts as TOUR_SCRIPTS } from "./scripts";

const STORAGE_KEY = "sharda.tour.completed";  // localStorage mirror
const SKIP_KEY = "sharda.tour.skipped";  // global skip flag
let _activeUser = null;  // captured from runTour({user}) for {name} interpolation

function _markSkippedLocal() {
  try { localStorage.setItem(SKIP_KEY, "true"); } catch { /* noop */ }
}
export function isSkippedLocal() {
  try { return localStorage.getItem(SKIP_KEY) === "true"; } catch { return false; }
}
function _clearLocalCompletion() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SKIP_KEY);
  } catch { /* noop */ }
}
export function restartTourResetLocal() { _clearLocalCompletion(); }

function _t(key, fallback) {
  const out = i18n.t(key, { defaultValue: fallback || key });
  return out || fallback || key;
}

function _interp(text) {
  if (!text) return text;
  const name = _activeUser?.name?.trim() || _activeUser?.first_name?.trim() || _activeUser?.email?.split("@")[0] || "";
  return String(text).replace(/\{\{?\s*name\s*\}?\}/g, name);
}

function _prefersReducedMotion() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false;
}

function _markLocal(role) {
  try {
    const cur = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    cur[role] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cur));
  } catch {
    /* localStorage disabled or full — safe to ignore. */
  }
}

function _localMarked(role) {
  try {
    const cur = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return !!cur[role];
  } catch {
    return false;
  }
}

/** Wait up to `timeoutMs` for an element matching `selector` (or a
 *  data-testid fallback) to appear. */
function waitForElement(selector, timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (!selector) return resolve(null);
    // Build a fallback selector: data-tour-id="foo" → also try data-testid="foo"
    const m = selector.match(/^\[data-tour-id=["']([^"']+)["']\]$/);
    const fallback = m ? `[data-testid="${m[1]}"]` : null;
    const find = () => document.querySelector(selector) || (fallback && document.querySelector(fallback));
    const found = find();
    if (found) return resolve(found);
    let resolved = false;
    const obs = new MutationObserver(() => {
      const el = find();
      if (el && !resolved) {
        resolved = true;
        obs.disconnect();
        resolve(el);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        obs.disconnect();
        resolve(null);
      }
    }, timeoutMs);
  });
}

/** When driver.js renders the popover it uses the SAME `selector` string we
 *  passed in. If the data-tour-id isn't present but a matching data-testid is,
 *  resolve the effective selector at step-render time. */
function _resolveSelector(selector) {
  if (!selector) return selector;
  if (document.querySelector(selector)) return selector;
  const m = selector.match(/^\[data-tour-id=["']([^"']+)["']\]$/);
  if (m) {
    const fb = `[data-testid="${m[1]}"]`;
    if (document.querySelector(fb)) return fb;
  }
  return selector;
}

/* --------------------- Tour Engine Enhancements --------------------- */
// Escape key handler + document click handler mounted on tour start,
// unmounted on tour end. Provides guaranteed dismissal.
let _keyHandler = null;

function _installKeyHandler() {
  if (_keyHandler || typeof window === "undefined") return;
  _keyHandler = (e) => {
    if (e.key === "Escape" && _activeDriver) {
      try { _activeDriver.destroy(); } catch { /* noop */ }
      _skip();
    }
  };
  window.addEventListener("keydown", _keyHandler);
}
function _uninstallKeyHandler() {
  if (_keyHandler && typeof window !== "undefined") {
    window.removeEventListener("keydown", _keyHandler);
  }
  _keyHandler = null;
}

let _activeDriver = null;
let _activeNavigate = null;
let _activeRoleKey = null;

async function _renderStep(steps, idx, navigate) {
  if (idx >= steps.length) return _finish();
  const step = steps[idx];

  // Navigate first if needed
  if (step.route && window.location.pathname !== step.route) {
    navigate(step.route);
    // Let React route mount
    await new Promise((r) => setTimeout(r, 350));
  }
  const el = await waitForElement(step.selector, step.timeoutMs || 3000);
  if (!el) {
    // Optional or missing: skip silently
    return _renderStep(steps, idx + 1, navigate);
  }

  const isLast = idx === steps.length - 1;
  const isFirst = idx === 0;
  const reduceMotion = _prefersReducedMotion();

  // Destroy any previous instance before creating new
  try {
    _activeDriver?.destroy();
  } catch {
    /* driver already destroyed, no-op. */
  }

  _activeDriver = driver({
    showProgress: true,
    showButtons: ["next", "previous", "close"],
    nextBtnText: isLast
      ? _t("tour.finish", "Finish")
      : _t("tour.next", "Next →"),
    prevBtnText: _t("tour.prev", "← Back"),
    doneBtnText: _t("tour.finish", "Finish"),
    progressText: `${idx + 1} / ${steps.length}`,
    overlayColor: "rgba(4,13,28,0.78)",
    animate: !reduceMotion,
    smoothScroll: !reduceMotion,
    popoverClass: "sharda-tour-popover",
    onPopoverRender: (popover, opts) => {
      // Append a Sharda chip to the footer for brand recognition
      const chip = document.createElement("div");
      chip.className = "sharda-tour-chip";
      chip.textContent = `SHARDA · ${_activeRoleKey || "TOUR"} · ${idx + 1}/${steps.length}`;
      popover.footer?.insertBefore(chip, popover.footer.firstChild);
    },
    steps: [
      {
        element: _resolveSelector(step.selector),
        popover: {
          title: _interp(_t(step.titleKey, step.titleFallback)),
          description: _interp(_t(step.bodyKey, step.bodyFallback)),
          side: step.side || "bottom",
          align: step.align || "center",
          showButtons: [
            ...(isFirst ? [] : ["previous"]),
            "next",
            "close",
          ],
          onNextClick: () => {
            _activeDriver?.destroy();
            _renderStep(steps, idx + 1, navigate);
          },
          onPrevClick: () => {
            _activeDriver?.destroy();
            _renderStep(steps, Math.max(0, idx - 1), navigate);
          },
          onCloseClick: () => {
            _activeDriver?.destroy();
            _skip();
          },
        },
      },
    ],
  });
  _activeDriver.drive();
}

async function _finish() {
  _uninstallKeyHandler();
  if (_activeRoleKey) {
    _markLocal(_activeRoleKey);
    try {
      await api.post("/me/tour/complete", { role: _activeRoleKey });
    } catch {
      /* server flag is nice-to-have; localStorage is the safety net */
    }
  }
  _activeDriver = null;
  _activeRoleKey = null;
  _activeNavigate = null;
}

async function _skip() {
  _uninstallKeyHandler();
  // Persist LOCALLY first so the tour never re-launches even if the
  // server call fails (offline, auth expired, etc.).
  if (_activeRoleKey) _markLocal(_activeRoleKey);
  _markSkippedLocal();
  try {
    await api.post("/me/tour/skip");
  } catch {
    /* server flag is nice-to-have. */
  }
  _activeDriver = null;
  _activeRoleKey = null;
  _activeNavigate = null;
}

/** Public API: start a tour. `roleOrNamed` is any registered key.
 *  Pass `user` so step text can interpolate `{name}` correctly. */
export function runTour(roleOrNamed, { navigate, user } = {}) {
  if (!navigate) {
    console.warn("[tourEngine] runTour requires a navigate function");
    return;
  }
  const key = String(roleOrNamed || "").toUpperCase();
  const script = TOUR_SCRIPTS[key] || TOUR_SCRIPTS.SALES;
  if (!script?.steps?.length) {
    console.warn("[tourEngine] no tour for", key);
    return;
  }
  _activeRoleKey = key;
  _activeNavigate = navigate;
  _activeUser = user || null;
  _installKeyHandler();
  _renderStep(script.steps, 0, navigate);
}

export function closeTour() {
  try {
    _activeDriver?.destroy();
  } catch {
    /* driver already destroyed, no-op. */
  }
  _activeDriver = null;
  _activeRoleKey = null;
}

export function hasCompletedLocally(role) {
  return _localMarked(String(role || "").toUpperCase());
}

export function tourScriptsForRole(role) {
  return TOUR_SCRIPTS[String(role || "").toUpperCase()] || null;
}

export const TOUR_KEYS = Object.keys(TOUR_SCRIPTS);
